import { EditorState } from '@tiptap/pm/state';
import { NodeRange } from '@tiptap/core';

/**
 * Judging by the character's current selection, determine if there is a node within the selection that matches any of
 * the supplied types. Also checks one child before and after in rare cases.
 */
export function areAnyNodesOfTypesNearby(state: EditorState, types: string[]) {
  const { from, to, empty } = state.selection;
  const nodeRanges: NodeRange[] = [];

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.isText) {
      return;
    }

    const relativeFrom = Math.max(from, pos);
    const relativeTo = Math.min(to, pos + node.nodeSize);

    nodeRanges.push({
      node,
      from: relativeFrom,
      to: relativeTo,
    });
  });

  /**
   * We check child before and after here to determine if the user's cursor has moved off a node into empty space.
   * No text, no nodes, no nothing.
   *
   * Alignment won't work in these scenarios.
   */
  const childBeforeFrom = state.doc.childBefore(from);
  const childAfterFrom = state.doc.childAfter(from);

  // This means the cursor has been moved off a node and up into empty space
  if (childBeforeFrom.node === null && childAfterFrom.node !== null) {
    nodeRanges.push({
      node: childAfterFrom.node,
      from: from,
      to: from + childAfterFrom.node.nodeSize,
    });
  }

  const childBeforeTo = state.doc.childBefore(to);
  const childAfterTo = state.doc.childAfter(to);

  // This means the cursor has been moved off a node and down into empty space
  if (childAfterTo.node === null && childBeforeTo.node !== null) {
    nodeRanges.push({
      node: childBeforeTo.node,
      from: from - childBeforeTo.node.nodeSize,
      to: to,
    });
  }

  /**
   * We need to detect when two nodes are in sequence without text between them. This will also cause the text alignment
   * button to be a noop.
   */
  if (
    from === to &&
    childBeforeFrom.node &&
    childAfterFrom.node &&
    // We may be able to relax the following to an `||`.
    types.includes(childBeforeFrom.node?.type.name) &&
    types.includes(childAfterFrom.node?.type.name)
  ) {
    nodeRanges.push({
      node: childBeforeFrom.node,
      from: from - childBeforeFrom.node.nodeSize,
      to: from,
    });

    nodeRanges.push({
      node: childAfterFrom.node,
      from: from,
      to: from + childAfterFrom.node.nodeSize,
    });
  }

  // We've got all the possible ranges, we just need to process + return now.

  const selectionRange = to - from;
  const matchedNodeRanges = nodeRanges.filter(nodeRange => {
    if (!types) {
      return true;
    }

    return types.includes(nodeRange.node.type.name);
  });

  if (empty) {
    return !!matchedNodeRanges.length;
  }

  const range = matchedNodeRanges.reduce(
    (sum, nodeRange) => sum + nodeRange.to - nodeRange.from,
    0
  );

  return range >= selectionRange;
}
